import { useEffect, useState } from "react";
import InputField from "../../common/InputField";
import { unitConversionAPI } from "../../utlis/apiService";
import { FormFooter } from "../../common/Shared";
import Model from "../../common/Model";

const PackingForm = ({
  initialData,
  onSave,
  onCancel,
  isEditing,
  setFormData,
  formData,
  isSaving = false,
}) => {
  const today = new Date().toISOString()?.split("T")[0];
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [isUnitConvData, setIsUnitConvData] = useState([]);
  const selectedUnit = isUnitConvData.find((u) => u.value === formData.unit_Conv_Id);

  useEffect(() => {
    const fetchUnitConvData = async () => {
      try {
        const res = await unitConversionAPI.getAll();
        setIsUnitConvData(
          res.data.map((u) => ({
            value: u.unit_Conv_Id,
            label: u.unit_Conv_Name,
            raw: u.conv_Value,
          })),
        );
      } catch (err) {
        console.error("Error loading dropdowns", err);
      }
    };
    fetchUnitConvData();
  }, []);

  const runCalculations = (currentFormData, unitsList) => {
    const pcs = parseFloat(currentFormData.pcs || 0);
    const totalWeight = parseFloat(currentFormData.total_Weight || 0);
    const wtCutLength = parseFloat(currentFormData.wt_Cut_Length || 0);

    const updated = { ...currentFormData };

    // final_CutLength_weight = total_Weight / pcs
    const finalCutLengthWeight = pcs && totalWeight ? (totalWeight / pcs) : 0;
    updated.final_CutLength_weight = finalCutLengthWeight ? finalCutLengthWeight.toFixed(2) : "";

    // conv_value from selected unit
    const selectedUnit = unitsList.find((u) => u.value === currentFormData.unit_Conv_Id);
    const convValue = selectedUnit?.raw || 1;

    // wt_12_FT = final_CutLength_weight * conv_value / wt_Cut_Length
    updated.wt_12_FT =
      finalCutLengthWeight && wtCutLength && convValue
        ? ((finalCutLengthWeight * convValue) / wtCutLength).toFixed(2)
        : "";

    return updated;
  };

  useEffect(() => {
    if (initialData) {
      const updatedForm = {
        bundle_No: initialData.bundle_No || "0",
        packing_Date: initialData.packing_Date
          ? initialData.packing_Date.split("T")[0]
          : today,
        order_No: initialData.order_No || "",
        dieNumber: initialData.dieNumber || "",
        die_Id: initialData.die_Id ?? null,
        batch_Id: initialData.batch_Id || "",
        casting_No: initialData.casting_No || "",
        cut_Length: initialData.cut_Length || "",
        wt_12_FT: initialData.wt_12_FT || initialData.wT_12_FT || "",
        wt_Cut_Length: initialData.wt_Cut_Length || initialData.wT_Cut_Length || "",
        pcs: initialData.pcs || "",
        total_Weight: initialData.total_Weight || "",
        series_Name: initialData.series_Name || "",
        remark: initialData.remark || "",
        unit_Conv_Id: initialData.unit_Conv_Id || "",
        final_CutLength_weight: initialData.final_CutLength_weight || "",
      };
      setFormData(runCalculations(updatedForm, isUnitConvData));
    }
  }, [initialData, isUnitConvData]);

  const handleChange = (e, field) => {
    const value = typeof e === "object" && e?.target ? e.target.value : e;
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      return runCalculations(updated, isUnitConvData);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, formData.bundle_No);
  };

  return (
    <Model
      isOpen={true}
      onClose={onCancel}
      name={isEditing ? "Edit Packing" : "Add Packing"}
      widthClass="max-w-6xl"
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col"
      >
        {/* Core Registry Metadata */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-6 border-b border-slate-100">
          <InputField
            label="Bundle No"
            value={formData.bundle_No}
            disabled
            className="bg-gray-100 text-gray-400 rounded-none border-gray-200 uppercase"
          />
          <InputField label="Batch ID" value={formData.batch_Id} disabled className="bg-gray-100" />
          <InputField label="Casting No" value={formData.casting_No} disabled className="bg-gray-100" />
          <InputField label="Order No" value={formData.order_No} disabled className="bg-gray-100" />
          <InputField label="Die No" value={formData.dieNumber} disabled className="bg-gray-100 font-black text-primary" />
          <InputField label="Series" value={formData.series_Name} disabled className="bg-gray-100" />
          <InputField label="Length" value={formData.cut_Length} disabled className="bg-gray-100 font-mono" />
          <InputField
            type="date"
            label="Packing Date"
            value={formData.packing_Date}
            onChange={(val) => handleChange(val, "packing_Date")}
            className="rounded-none border-gray-200 focus:border-primary"
          />
        </div>

        {/* Industrial Metrics & Measurements */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Total Weight"
              value={formData.total_Weight}
              onChange={(val) => handleChange(val, "total_Weight")}
              className="focus:border-primary rounded-none"
              unit="KG"
            />
            <InputField
              label="Pcs"
              value={formData.pcs}
              onChange={(val) => handleChange(val, "pcs")}
              className="focus:border-primary rounded-none font-black"
              unit="PCS"
            />
            <div className="relative group">
              <InputField
                label="WT/12 FT"
                value={formData.wt_12_FT}
                disabled
                className="bg-gray-50 font-black text-gray-900 rounded-none border-gray-200"
              />
            </div>
            <InputField
              label="Final Cut Length"
              value={formData.wt_Cut_Length}
              onChange={(val) => handleChange(val, "wt_Cut_Length")}
              className="focus:border-primary rounded-none"
            />
            <div className="relative group">
              <InputField
                type="select"
                label="Unit"
                options={isUnitConvData}
                value={formData.unit_Conv_Id}
                onChange={(val) => handleChange(val, "unit_Conv_Id")}
                className="rounded-none border-gray-200 focus:border-primary"
              />
              {selectedUnit && (
                <div className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-primary/5 border border-primary/10 text-[10px] font-black text-primary uppercase tracking-[1.5px]">
                  <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                  Unit Conversion Value: {selectedUnit.raw}
                </div>
              )}
            </div>
            <div className="relative group">
              <InputField
                label="Cut Length"
                value={formData.final_CutLength_weight}
                disabled
                className="bg-slate-100 font-black text-primary rounded-none border-gray-200"
              />
            </div>
          </div>

          <div className="flex flex-col">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[2px] mb-2">
              Remarks
            </label>
            <textarea
              className="w-full flex-1 p-4 border border-gray-200 text-[12px] font-medium focus:border-primary transition-all outline-none rounded-none resize-none placeholder:text-gray-300"
              placeholder="Document fulfillment anomalies or structural deviations..."
              value={formData.remark}
              onChange={(e) => handleChange(e.target.value, "remark")}
            />
          </div>
        </div>

        <FormFooter
          onCancel={onCancel}
          onSubmit={handleSubmit}
          submitText="Save"
          isSaving={isSaving}
        />
      </form>
    </Model>
  );
};

export default PackingForm;
